// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Save, X, Eye, LayoutDashboard } from 'lucide-react';

interface EditActionButtonsProps {
  onSave: () => void;
  onCancel: () => void;
  onShowDashboard: () => void;
  onVisualize: () => void;
  isSaving: boolean;
  hasChanges: boolean;
  accentColor?: string;
  documentType: 'quote' | 'invoice';
}

/**
 * Composant réutilisable pour tous les boutons d'actions en mode édition
 * V2 Design System - Volvo/Teal Theme avec glassmorphism
 */
export const EditActionButtons: React.FC<EditActionButtonsProps> = ({
  onSave,
  onCancel,
  onShowDashboard,
  onVisualize,
  isSaving,
  hasChanges,
  documentType: _documentType,
}) => {
  const { t } = useTranslation();
  return (
    <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
      {/* Container glassmorphism - même style que les Cards V2 */}
      <div className="bg-white/30 backdrop-blur-xl rounded-2xl shadow-xl border border-white/40 p-3 flex flex-col gap-2">

        {/* Gérer (Dashboard) */}
        <button
          onClick={onShowDashboard}
          className="px-4 py-2.5 bg-white/60 hover:bg-white/90 rounded-xl text-gray-700
            transition-all duration-300 font-medium text-sm flex items-center gap-2
            border border-white/50 hover:border-white/70 hover:shadow-md"
          title={t('templates.actions.manageTitle')}
        >
          <LayoutDashboard className="w-4 h-4" />
          <span>{t('templates.common.manage')}</span>
        </button>

        {/* Visualiser */}
        <button
          onClick={onVisualize}
          className="px-4 py-2.5 bg-white/60 hover:bg-white/90 rounded-xl text-gray-700
            transition-all duration-300 font-medium text-sm flex items-center gap-2
            border border-white/50 hover:border-white/70 hover:shadow-md"
          title={t('templates.actions.visualizeTitle')}
        >
          <Eye className="w-4 h-4" />
          <span>{t('templates.common.visualize')}</span>
        </button>

        {/* Séparateur */}
        <div className="h-px bg-white/40 my-1" />

        {/* Enregistrer - Bouton principal teal */}
        <button
          onClick={onSave}
          disabled={isSaving || !hasChanges}
          className={`px-4 py-2.5 rounded-xl text-white transition-all duration-300
            font-semibold text-sm flex items-center gap-2 justify-center
            ${isSaving || !hasChanges
              ? 'bg-gray-400/80 cursor-not-allowed'
              : 'bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/30 hover:shadow-xl hover:-translate-y-0.5'
            }`}
          title={hasChanges ? t('templates.actions.saveTitle') : t('templates.actions.noChangesTitle')}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              <span>{t('templates.common.saving')}</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>{t('templates.common.saveRecord')}</span>
            </>
          )}
        </button>

        {/* Annuler */}
        <button
          onClick={onCancel}
          className="px-4 py-2.5 bg-white/60 hover:bg-white/90 border border-white/50 hover:border-gray-300 rounded-xl
            text-gray-600 hover:text-gray-800 transition-all duration-300
            font-medium text-sm flex items-center gap-2 hover:shadow-md"
          title={t('templates.actions.cancelTitle')}
        >
          <X className="w-4 h-4" />
          <span>{t('templates.common.cancel')}</span>
        </button>
      </div>
    </div>
  );
};
